import { Typography } from "@mui/material";
import React, { useEffect, useMemo, useCallback, useRef } from "react";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Avatar,
  Checkbox,
  FormControlLabel,
  Divider,
  Card,
  CardMedia,
  Chip,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import theme from "../../utils/theme";
import BreadcrumbsNav from "../../components/common/BreadCrumbNav";
import GiooleftCard from "./gioo_plots_left_card";
import GiooPlotsDashboard from "./GiooplotDashboard";
import GiooplotsDescription from "./GiooplotsDescription";
import GiooPropertyLoaction from "./GiooPlotsLocation";
import ImageViewerCard from "./GiooimageViwerCard";

import { get_geo_property, apply_coupon, get_settings } from "../../api/ControllApi";
import { toast } from "react-toastify";
import { useModal } from "../../context/ModalContext";
import { usePayment } from "../../Config/Razar_pay";
import { LocationOn } from "@mui/icons-material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useUser } from "../../context/LoginDetail";
import { pay_gioo_plot } from "../../api/Payment";
import { get_page } from "../../api/PageApi";

// ─── Static data outside component to avoid re-creation on each render ───────
const SOLD_BG = "#cfcfcf";
const SOLD_BORDER = "#b0b0b0";
const SOLD_TEXT = "#7a7a7a";
const BATCH_SIZE = 100;

const steps = [
  { icon: "/assets/images/slot.png", label: "Select Your Slot" },
  { icon: "/assets/images/payment_verified.png", label: "Get payment Verified" },
  { icon: "/assets/images/process.png", label: "Lease Registration" },
  { icon: "/assets/images/success.png", label: "Plot is Registered" },
];

// ─── Arrow components outside parent to avoid recreation ─────────────────────
function NextArrow({ onClick }) {
  return (
    <Box onClick={onClick} sx={{ position: "absolute", right: { xs: "-10px", md: "-25px" }, top: "50%", transform: "translateY(-50%)", cursor: "pointer", zIndex: 5, bgcolor: "#fff", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", p: 0.5 }}>
      <img src="/assets/images/right_arrow.png" alt="next" width={30} />
    </Box>
  );
}

function PrevArrow({ onClick }) {
  return (
    <Box onClick={onClick} sx={{ position: "absolute", left: { xs: "-10px", md: "-25px" }, top: "50%", transform: "translateY(-50%)", cursor: "pointer", zIndex: 5, bgcolor: "#fff", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", p: 0.5 }}>
      <img src="/assets/images/left_arrow.png" alt="prev" width={30} />
    </Box>
  );
}

// ─── Memoized PlotItem to avoid re-rendering all plots on selection ───────────
const PlotItem = React.memo(({ unitNumber, isBooked, isAdminBlocked, isSelected, isSoldOut, onPlotClick }) => {
  const isDisabled = isSoldOut || isBooked || isAdminBlocked;

  let bgcolor = theme.palette.primary.light;
  if (isSoldOut) bgcolor = SOLD_BG;
  else if (isBooked || isAdminBlocked) bgcolor = "#36970dff";
  else if (isSelected) bgcolor = theme.palette.secondary.main;

  return (
    <Grid item xs={6} sm={4} md={1.2} lg={1.5} justifyItems="center">
      <Box
        sx={{ width: 50, opacity: isSoldOut ? 0.5 : isDisabled ? 0.6 : 1, cursor: isDisabled ? "not-allowed" : "pointer" }}
        onClick={isDisabled ? undefined : (e) => onPlotClick(unitNumber, e)}
      >
        <Box sx={{ bgcolor, p: 2, borderRadius: "8px", border: `1px solid ${isSoldOut ? SOLD_BORDER : "#ccc"}`, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: isSoldOut ? SOLD_TEXT : "white" }}>
            {unitNumber}
          </Typography>
        </Box>
      </Box>
    </Grid>
  );
});

const maskPhone = (phone) => {
  if (!phone) return "N/A";
  const cleanPhone = String(phone).trim();
  if (cleanPhone.length <= 4) return cleanPhone;
  const first2 = cleanPhone.slice(0, 2);
  const last2 = cleanPhone.slice(-2);
  const maskedLength = cleanPhone.length - 4;
  const xString = "x".repeat(maskedLength > 0 ? maskedLength : 5);
  return `${first2}${xString}${last2}`;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "N/A";
  const d = new Date(dateStr);
  const pad = (num) => String(num).padStart(2, "0");
  const date = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  let hours = d.getHours();
  const minutes = pad(d.getMinutes());
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  return `${date} ${pad(hours)}:${minutes} ${ampm}`;
};

// ─── Memoized BuyerItem ───────────────────────────────────────────────────────
const BuyerItem = React.memo(({ person }) => (
  <Box
    sx={{
      p: 2,
      borderBottom: "1px solid",
      borderColor: "secondary.light",
      display: "flex",
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 2,
      width: "100%",
      minHeight: 100,
    }}
  >
    <Avatar
      sx={{ width: 48, height: 48, border: "2px solid #92AF5D" }}
      src={person?.avatar || "/assets/images/profile2.png"}
    />
    <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", pr: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ color: "#1e3d59" }}>
          {person.name}
        </Typography>
        {person?.gio_transaction?.length > 0 && (
          <Chip
            label={`${person.gio_transaction.length} transactions`}
            size="small"
            sx={{
              bgcolor: "rgba(146, 175, 93, 0.1)",
              color: "#92AF5D",
              fontWeight: 600,
              border: "1px solid #92AF5D",
              height: 24,
            }}
          />
        )}
      </Box>
      <Typography color="text.secondary" sx={{ fontSize: 13, mt: 0.5 }}>
        Phone: <strong>{maskPhone(person.phone)}</strong>
      </Typography>
      {person?.gio_transaction && person.gio_transaction.map((tx, idx) => (
        <Box
          key={idx}
          sx={{
            mt: 1.5,
            px: 3,
            py: 1.5,
            bgcolor: "#f4f8fa",
            borderRadius: "8px",
            border: "1px solid #d2e4f7",
            width: "96%",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap", width: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <Typography variant="body2" sx={{ color: "grey.800", fontSize: "0.875rem" }}>
                Buying Price: <strong>₹{Number(tx.amount).toLocaleString("en-IN")}</strong>
              </Typography>
              <Typography variant="caption" sx={{ color: "grey.600", fontSize: "0.775rem", mt: 0.2 }}>
                {formatDateTime(tx.created_at)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
              <Typography variant="body2" sx={{ color: "#2e7d32", fontWeight: "bold", fontSize: "0.875rem" }}>
                Selling Price: <strong>₹{Number(tx.after_amount).toLocaleString("en-IN")}</strong>
              </Typography>
              <Typography variant="caption" sx={{ color: "grey.600", fontSize: "0.775rem", mt: 0.2 }}>
                {formatDateTime(tx.after_two_year)}
              </Typography>
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
));

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

// ─── Main Component ───────────────────────────────────────────────────────────
const Gioo_plots_property = () => {
  const { requireLogin } = useModal();
  const { id } = useParams();
  const navigate = useNavigate();
  const { handlePayment } = usePayment();
  const { user } = useUser();

  // ── State ──────────────────────────────────────────────────────────────────
  const [geoDetails, setGeoDetails] = useState({});
  const [buyerlist, setBuyerList] = useState([]);
  const [unitSplit, setUnitSplit] = useState(0);
  const [batchIndex, setBatchIndex] = useState(0);
  const [area, setArea] = useState(0);
  const [loading, setLoading] = useState(true);
  const [adminBlockCount, setAdminBlockcount] = useState([]);
  const [slots, setSlots] = useState([]);
  const [booking, setBooking] = useState([]);
  const [adminBlocks, setAdminBlocks] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [pricePerPlot, setPricePerPlot] = useState(0);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponData, setCouponData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [privacyContent, setPrivacyContent] = useState("");
  const [termsContent, setTermsContent] = useState("");
  const [useWallet, setUseWallet] = useState(false);
  const [walletAmount, setWalletAmount] = useState("");
  const [showAllBuyers, setShowAllBuyers] = useState(false);
  const [timeFilter, setTimeFilter] = useState("all");

  // ── Parallel data fetch on mount ──────────────────────────────────────────
  useEffect(() => {
    // Fire all 3 API calls in parallel instead of sequentially
    Promise.all([
      get_geo_property(id),
      get_settings(),
      // fetch both pages in parallel
      Promise.all([
        get_page("gioo_nano_plots_booking_privacy_policy"),
        get_page("gioo_nano_plots_booking_terms_and_condition"),
      ]),
    ])
      .then(([geoRes, settingsRes, [privacyRes, termsRes]]) => {
        // geo property
        const data = geoRes?.data;
        setGeoDetails(data);
        setSlots(data?.booking);
        setBuyerList(data?.user);
        setUnitSplit(data?.unit_spilt || 0);
        setAdminBlocks(data?.adminblock?.units || []);
        setArea(data?.area || 0);
        setPricePerPlot(data?.price || 0);
        setAdminBlockcount(data?.adminblock?.units?.split(",") || []);
        setBooking(data?.booking);

        // settings
        setSettings(settingsRes?.data);

        // pages
        if (privacyRes?.success && privacyRes?.data?.content) setPrivacyContent(privacyRes.data.content);
        if (termsRes?.success && termsRes?.data?.content) setTermsContent(termsRes.data.content);
      })
      .catch((err) => { })
      .finally(() => setLoading(false));
  }, [id]); // added id as dependency

  // ── Derived / memoised values ──────────────────────────────────────────────

  // Flatten booked units once — O(n) instead of re-running on every render
  const bookedUnits = useMemo(() => [
    ...new Set(
      booking.flatMap((b) => b.units?.split(",").filter(Boolean).map(Number) || [])
    ),
  ], [booking]);

  const totalBookedUnits = useMemo(() =>
    booking.reduce((count, b) => (!b.units ? count : count + b.units.split(",").length), 0),
    [booking]
  );

  const maxBatchIndex = useMemo(() => Math.ceil(area / BATCH_SIZE) - 1, [area]);

  const { currentUnits, startUnit, endUnit } = useMemo(() => {
    const start = batchIndex * BATCH_SIZE + 1;
    const end = Math.min((batchIndex + 1) * BATCH_SIZE, area);
    return {
      currentUnits: Array.from({ length: end - start + 1 }, (_, i) => start + i),
      startUnit: start,
      endUnit: end,
    };
  }, [batchIndex, area]);

  const firstAvailableUnit = useMemo(() => {
    if (!area || area <= 0) return 1;
    for (let i = 1; i <= area; i++) {
      if (!bookedUnits.includes(i) && !adminBlocks.includes(i)) return i;
    }
    return 1;
  }, [area, bookedUnits, adminBlocks]);

  // Jump to the batch that contains the first available unit
  useEffect(() => {
    if (!firstAvailableUnit || area <= 0) return;
    setBatchIndex(Math.floor((firstAvailableUnit - 1) / BATCH_SIZE));
  }, [firstAvailableUnit, area]);

  // ── Price calculation ──────────────────────────────────────────────────────
  const priceCalculation = useMemo(() => {
    let basePrice = pricePerPlot * selectedUnits.length || 0;
    let specialDiscount = 0;
    let couponDiscount = 0;
    let walletDiscount = 0;

    if (parseInt(settings?.discount_max_cost) && parseInt(basePrice) >= parseInt(settings.discount_max_cost)) {
      const percentage = parseFloat(settings.discount_percentage) || 0;
      specialDiscount = (basePrice * percentage) / 100;
      basePrice -= specialDiscount;
    }

    if (couponData?.amount) {
      couponDiscount = parseFloat(couponData.amount);
      basePrice -= couponDiscount;
    }

    const payableBeforeWallet = basePrice;

    if (useWallet && walletAmount) {
      const maxWalletPossible = Math.max(0, payableBeforeWallet);
      walletDiscount = Math.min(parseFloat(walletAmount) || 0, maxWalletPossible);
      basePrice -= walletDiscount;
    }

    return {
      finalAmount: Math.max(0, basePrice),
      specialDiscount,
      couponDiscount,
      walletDiscount,
      payableBeforeWallet,
    };
  }, [pricePerPlot, selectedUnits.length, settings, couponData, useWallet, walletAmount]);

  const finalPayableAmount = priceCalculation.finalAmount;

  const serviceChargePercent = parseFloat(parseFloat(settings?.gioo_service_charge || 0).toFixed(2));
  const igstPercent = parseFloat(parseFloat(settings?.gioo_igst || 0).toFixed(2));

  const serviceChargeAmount = parseFloat((finalPayableAmount * (serviceChargePercent / 100)).toFixed(2));
  const igstAmount = parseFloat((finalPayableAmount * (igstPercent / 100)).toFixed(2));
  const totalAmount = parseFloat((finalPayableAmount + serviceChargeAmount + igstAmount).toFixed(2));
  const filteredBuyers = useMemo(() => {
    const getFilteredTransactions = (transactions, filter) => {
      if (!transactions) return [];
      if (filter === "all") return transactions;

      const now = new Date();
      let daysLimit = 0;
      if (filter === "15days") daysLimit = 15;
      else if (filter === "1month") daysLimit = 30;

      const limitDate = new Date();
      limitDate.setDate(now.getDate() - daysLimit);

      return transactions.filter(tx => {
        if (!tx.created_at) return false;
        const txDate = new Date(tx.created_at);
        return txDate >= limitDate;
      });
    };

    return buyerlist
      .map(person => {
        const filteredTx = getFilteredTransactions(person.gio_transaction, timeFilter);
        return {
          ...person,
          gio_transaction: filteredTx
        };
      })
      .filter(person => person.gio_transaction.length > 0);
  }, [buyerlist, timeFilter]);

  // ── Callbacks (stable references) ─────────────────────────────────────────
  const handlePlotClick = useCallback((unitNumber, event) => {
    if (event) event.stopPropagation();
    setSelectedUnits((prev) => {
      const isDeselecting = prev.includes(unitNumber);
      if (isDeselecting) {
        setCouponData(null);
        setCouponCode("");
        setUseWallet(false);
        setWalletAmount("");
        return prev.filter((num) => num !== unitNumber);
      }
      return [...prev, unitNumber];
    });
  }, []);

  const calculatePriceBeforeCoupon = useCallback(() => {
    let basePrice = pricePerPlot * selectedUnits.length || 0;
    if (parseInt(settings?.discount_max_cost) && parseInt(basePrice) >= parseInt(settings.discount_max_cost)) {
      const percentage = parseFloat(settings.discount_percentage) || 0;
      basePrice -= basePrice * percentage / 100;
    }
    return basePrice;
  }, [pricePerPlot, selectedUnits.length, settings]);

  const handleApplyCoupon = useCallback(async () => {
    if (selectedUnits.length === 0) { toast.error("Select the plot and apply coupon"); return; }
    if (!couponCode) { toast.error("Please enter a coupon code"); return; }
    setCouponLoading(true);
    try {
      const res = await apply_coupon(couponCode);
      if (res.status) {
        const currentPrice = calculatePriceBeforeCoupon();
        if (parseFloat(res.data.amount) > currentPrice) { toast.error("Coupon is more than payable amount"); return; }
        setCouponData(res.data);
        toast.success(res.message);
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid or expired coupon");
    } finally {
      setCouponLoading(false);
    }
  }, [selectedUnits.length, couponCode, calculatePriceBeforeCoupon]);

  const handleRemoveCoupon = useCallback(() => {
    setCouponData(null);
    setCouponCode("");
    toast.info("Coupon removed");
  }, []);

  const handleBuyGio = useCallback(async (property_id, units) => {
    if (!units || units === "") {
      toast.error("Atleast select one to payment");
      return;
    }
    if (finalPayableAmount < 0) { toast.error("Invalid payable amount"); return; }
    const payloadExtras = {
      coupon_id: couponData?.coupon_code || null,
      special_discount: priceCalculation.specialDiscount || 0,
    };
    try {
      if (finalPayableAmount === 0) {
        const payLoad = {
          status: "success",
          payment_id: "wallet_full_" + Date.now(),
          transaction_details: { type: "wallet" },
          amount: 0,
          wallet_amount: priceCalculation.walletDiscount || 0,
          property_id,
          units,
          ...payloadExtras,
        };
        const result = await pay_gioo_plot(payLoad);
        toast.success(result?.message || "Purchase successful!");
        navigate("/dashboard/buying-list?tab=gioo");
      } else {
        const customPayloadExtras = { ...payloadExtras, wallet_amount: priceCalculation.walletDiscount || 0, transaction_details: { type: "razorpay" } };
        await handlePayment(totalAmount, property_id, units, customPayloadExtras);
        navigate("/dashboard/buying-list?tab=gioo");
      }
    } catch (error) {

    }
  }, [finalPayableAmount, totalAmount, couponData, priceCalculation, handlePayment, navigate]);

  const unitsString = useMemo(() => selectedUnits.join(","), [selectedUnits]);

  // ── Legend counts (memoised) ───────────────────────────────────────────────
  const legendItems = useMemo(() => [
    { label: "Booked", count: (bookedUnits?.length || 0) + (adminBlockCount?.length || 0), color: "#36970dff" },
    { label: "Selected", count: selectedUnits.length || 0, color: theme.palette.secondary.main },
    { label: "Available", count: area - ((bookedUnits?.length || 0) + (adminBlockCount?.length || 0)), color: theme.palette.primary.light },
  ], [bookedUnits.length, adminBlockCount.length, selectedUnits.length, area]);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Container sx={{ py: 5 }}>
      <Box sx={{ mb: 3 }}>
        <BreadcrumbsNav />
      </Box>

      <Box sx={{ display: { xs: "block", md: "flex" }, gap: { xs: 2, md: 3 }, alignItems: { md: "stretch" } }}>
        <Box>
          <GiooleftCard
            title={geoDetails?.name}
            loading={loading}
            images={geoDetails?.image}
            lat={geoDetails?.lat}
            lng={geoDetails?.long}
            map_set={geoDetails?.map_set}
            propertyName={geoDetails?.name}
            youtubeLink={geoDetails?.youtube_link}
            whatsappNumber={settings?.whatsapp}
          />
        </Box>
        <Box sx={{ display: { md: "none" } }}><br /></Box>
        <GiooplotsDescription
          area={geoDetails?.area}
          per_sq={geoDetails?.unit_spilt}
          price={geoDetails?.price}
          plottype={geoDetails?.property_type?.category_name}
          totalPrice={geoDetails?.total_price}
          description={geoDetails?.description}

          overallbooked={geoDetails?.overall_booked}
          weeklybooked={geoDetails?.weakly_booked}
        />
      </Box>

      <Box>

      </Box>

      <Box sx={{ my: 4 }}>
        <GiooPropertyLoaction
          address={geoDetails?.address}
          uldNumber={geoDetails?.uld_no}
          amenity={geoDetails?.amenity}
          commonfacility={geoDetails?.commonfacility}
          loading={loading}
          nearbyLoction={geoDetails?.nearbyLocations}
        />
      </Box>

      {/* ── How It Works + Buyer List ─────────────────────────────────────── */}
      <Box sx={{ mt: 5, display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: { xs: "center", md: "center" }, justifyContent: "center", gap: { xs: 4, md: 6 }, width: "100%" }}>
        {/* Blueprint image */}
        <Box sx={{ flex: { xs: "0 0 100%", sm: "0 0 80%", md: "0 0 40%" }, maxWidth: { xs: "100%", sm: "400px", md: "420px", lg: "480px" }, width: "100%", display: "flex", justifyContent: "center" }}>
          <Container>
            <ImageViewerCard isStructure={false} image={geoDetails?.blue_print || "/assets/images/plot_image.png"} />
          </Container>
        </Box>

        {/* Steps + Buyers */}
        <Box sx={{ flex: { xs: "0 0 100%", md: "0 0 55%" }, maxWidth: { xs: "100%", md: "600px", lg: "750px" }, width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, mb: 2, mt: { xs: 4, md: 0 } }}>
            <Typography align="center" variant="h5" sx={{ fontWeight: 600 }}>
              How It Works
            </Typography>
            {settings?.how_it_work_youtube_link && (
              <Button
                variant="contained"
                size="small"
                onClick={() => window.open(settings.how_it_work_youtube_link, "_blank")}
                sx={{
                  bgcolor: "#92AF5D",
                  color: "#fff",
                  textTransform: "capitalize",
                  borderRadius: 2,
                  px: 2,
                  fontWeight: 600,
                  fontSize: 13,
                  "&:hover": { bgcolor: "#7E9F40" },
                }}
              >
                More
              </Button>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: { xs: "center", sm: "flex-start" }, justifyContent: "center", flexDirection: { xs: "column", sm: "row" }, mt: 2, position: "relative" }}>
            <Box sx={{ position: "absolute", top: 40, left: 90, right: 90, display: { xs: "none", sm: "block" }, borderBottom: "2px dashed #92AF5D", zIndex: 0 }} />
            {steps.map((step, index) => (
              <Box key={index} sx={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative", flex: "1 1 0", zIndex: 1, px: 2 }}>
                <Box sx={{ width: 80, height: 80, borderRadius: "50%", border: "3px solid #92AF5D", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#fff" }}>
                  <img src={step.icon} alt={step.label} style={{ width: 40, height: 40 }} />
                </Box>
                <Typography variant="h6" sx={{ mt: 1.5, textAlign: "center", mx: 2, color: "text.secondary", fontWeight: 400, maxWidth: 250, fontSize: 14 }}>
                  {step.label}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              mt: { xs: 2, sm: 3 },
              mb: { xs: 2, sm: 3 },
              gap: 2,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, color: "#1e3d59" }}>
              Our Buyers List
            </Typography>

            <Box
              sx={{
                display: "flex",
                bgcolor: "#f1f3f4",
                borderRadius: "30px",
                p: 0.5,
              }}
            >
              {[
                { label: "All", value: "all" },
                { label: "15 Days", value: "15days" },
                { label: "1 Month", value: "1month" },
              ].map((tab) => (
                <Button
                  key={tab.value}
                  onClick={() => setTimeFilter(tab.value)}
                  size="small"
                  sx={{
                    borderRadius: "30px",
                    px: 2,
                    py: 0.5,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    bgcolor: timeFilter === tab.value ? "#92AF5D" : "transparent",
                    color: timeFilter === tab.value ? "#fff" : "#5f6368",
                    "&:hover": {
                      bgcolor: timeFilter === tab.value ? "#7E9F40" : "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </Box>
          </Box>

          <Box sx={{ width: "100%", p: 2, mb: 2, mr: 5, borderRadius: 3, boxShadow: 2, backgroundColor: "#fff" }}>
            <Box sx={{ maxHeight: 350, overflowY: "auto" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
                {filteredBuyers.length > 0
                  ? (showAllBuyers ? filteredBuyers : filteredBuyers.slice(0, 1)).map((person, index) => <BuyerItem key={index} person={person} />)
                  : <Typography sx={{ p: 2 }}>No Buyers Found</Typography>
                }
              </Box>
            </Box>
            {filteredBuyers.length > 1 && (
              <Box sx={{ mt: 2 }}>
                <Button
                  onClick={() => setShowAllBuyers(!showAllBuyers)}
                  fullWidth
                  sx={{
                    bgcolor: "#92AF5D",
                    color: "#fff",
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "30px",
                    py: 1,
                    fontSize: "0.95rem",
                    "&:hover": { bgcolor: "#7E9F40" },
                  }}
                >
                  {showAllBuyers ? "View Less" : `View More • ${filteredBuyers.length - 1} more`}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Plot image */}
      <Box sx={{ mt: 4, mb: { xs: 10, md: 0 }, height: { xs: 300, sm: 400, md: 500 } }}>
        <ImageViewerCard isStructure={true} image={geoDetails?.plot_image || "/assets/images/plot_image.png"} />
      </Box>
      <Box>
        <GiooPlotsDashboard
          totalUnits={area || 0}
          loading={loading}
          weeklyGraph={geoDetails?.weeklyGraph}
          monthlyGraph={geoDetails?.monthlyGraph}
          overallbooked={geoDetails?.overall_booked}
          weeklybooked={geoDetails?.weakly_booked}
        />
      </Box>

      {/* ── Plots + Payment Sidebar ───────────────────────────────────────── */}
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, width: "100%" }}>

        {/* Plots grid */}
        <Box sx={{ my: 3, width: "100%", backgroundColor: "white", borderRadius: "20px", p: 2 }}>
          {/* Header */}
          <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", width: "100%", mb: 2, gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, fontSize: 20 }}>GreenHeap Plots</Typography>
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
              {legendItems.map((item, idx) => (
                <Box key={idx} sx={{ display: "flex", alignItems: "center" }}>
                  <Box sx={{ height: 20, width: 20, borderRadius: "5px", bgcolor: item.color }} />
                  <Typography sx={{ ml: 1, fontSize: 15, color: "text.secondary" }}>
                    {item.label} ({item.count})
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Typography sx={{ mb: 2, fontWeight: 500, textAlign: "center" }}>
            {geoDetails?.sold_status === 1 && (
              <Box sx={{ mb: 2, px: 2, py: 1.5, borderRadius: 2, bgcolor: "rgba(244, 67, 54, 0.1)", border: "1px solid", borderColor: "error.main", display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontWeight: 600, color: "error.main", fontSize: 15 }}>Plots Sold</Typography>
                <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
                  This plot has been sold. If you invested in it, please check your dashboard under Buying List.
                </Typography>
              </Box>
            )}
            Showing {startUnit}-{endUnit} of {area} units
          </Typography>

          {/* ── Plot cells — each is memoised so only changed cells re-render ── */}
          <Grid container spacing={1}>
            {currentUnits.map((unitNumber) => (
              <PlotItem
                key={unitNumber}
                unitNumber={unitNumber}
                isBooked={bookedUnits.includes(unitNumber)}
                isAdminBlocked={adminBlocks.includes(unitNumber)}
                isSelected={selectedUnits.includes(unitNumber)}
                isSoldOut={geoDetails?.sold_status === 1}
                onPlotClick={handlePlotClick}
              />
            ))}
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "center", gap: 3, mt: 2.4 }}>
            <Button variant="outlined" disabled={batchIndex === 0} onClick={() => setBatchIndex((i) => Math.max(i - 1, 0))}>Previous</Button>
            <Button variant="outlined" disabled={batchIndex >= maxBatchIndex} onClick={() => setBatchIndex((i) => Math.min(i + 1, maxBatchIndex))}>Next</Button>
          </Box>
        </Box>

        {/* ── Payment / booking sidebar ─────────────────────────────────── */}
        <Box sx={{ my: 2, backgroundColor: "#FFF9E5", borderRadius: "20px", minWidth: { xs: "100%", sm: "100%", md: "300px" }, height: "auto", minHeight: 250, p: { xs: 2, sm: 3, md: 3 } }}>
          <Box sx={{ alignItems: "start", padding: 0 }}>

            <Box sx={{ my: 1, display: "flex", gap: 1, alignItems: "center" }}>
              <LocationOn sx={{ fontSize: 25, color: "#608900" }} />
              <Typography variant="h6" sx={{ fontSize: { xs: 14, sm: 16, md: 18 }, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                Property Location
              </Typography>
            </Box>

            <Box sx={{ my: 2 }}>
              <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14, md: 16 } }}>
                {geoDetails?.address || ""}
              </Typography>
            </Box>

            <Box sx={{ my: 2 }}>
              <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14, md: 16 } }}>
                <strong style={{ color: theme.palette.primary.main }}>ULPIN Number:</strong> {geoDetails?.uld_no || ""}
              </Typography>
            </Box>

            <Divider sx={{ borderStyle: "dashed", borderColor: "gray", borderWidth: 1, marginY: 2 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14, md: 16 } }}>GreenHeap Plots</Typography>
              {geoDetails?.verify_status === 1 && (
                <Box sx={{ my: 1, backgroundColor: "white", color: "grey", padding: 0.5, borderRadius: 20, display: "flex", alignItems: "center" }}>
                  <img src="/assets/images/verified.png" width={17} alt="verified" />
                  <Typography sx={{ fontSize: 10, ml: 0.5 }}>Approved Plot</Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <img src="/assets/images/calender.png" style={{ height: 18 }} alt="calendar" />
                <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14, md: 16 } }}>
                  {formatDate(geoDetails?.created_at)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <img src="/assets/images/location.png" style={{ height: 35 }} alt="location" />
                <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14, md: 16 } }}>
                  {area || 0} Units
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ borderStyle: "dashed", borderColor: "gray", borderWidth: 1, marginY: 2 }} />

            <Typography variant="h6" sx={{ fontSize: { xs: 14, sm: 16, md: 18 }, fontWeight: 500 }}>
              Price of Selected Plots
            </Typography>

            <Box sx={{ my: 0.3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ color: "grey", fontSize: { xs: 12, sm: 14, md: 16 } }}>Plot Area:</Typography>
              <Typography sx={{ color: theme.palette.primary.main, fontSize: { xs: 12, sm: 14, md: 16 } }}>{geoDetails?.price}</Typography>
            </Box>

            <Box sx={{ my: 0.3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ color: "grey", fontSize: { xs: 12, sm: 14, md: 16 } }}>Selected Plots:</Typography>
              <Typography sx={{ color: theme.palette.primary.main, fontSize: { xs: 12, sm: 14, md: 16 } }}>{selectedUnits.length || 0}</Typography>
            </Box>

            <Divider sx={{ borderStyle: "dashed", borderColor: "gray", borderWidth: 1, marginY: 2 }} />
            <Divider sx={{ borderStyle: "dashed", borderColor: "gray", borderWidth: 1, marginY: 2 }} />

            {/* Coupon */}
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>Have a Coupon?</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <input
                  type="text"
                  placeholder="Enter Coupon Code"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  disabled={!!couponData}
                  style={{ padding: "8px 12px", borderRadius: "4px", border: "1px solid #ccc", flex: 1, fontSize: "14px", backgroundColor: couponData ? "#f5f5f5" : "white" }}
                />
                {couponData ? (
                  <Button variant="outlined" size="small" onClick={handleRemoveCoupon} color="error" sx={{ textTransform: "none" }}>Remove</Button>
                ) : (
                  <Button variant="contained" size="small" onClick={handleApplyCoupon} disabled={couponLoading || !couponCode} sx={{ textTransform: "none", bgcolor: theme.palette.primary.main }}>
                    {couponLoading ? "..." : "Apply"}
                  </Button>
                )}
              </Box>
              {couponData && (
                <Typography sx={{ fontSize: 12, color: "green", mt: 0.5 }}>
                  Coupon Applied: ₹{couponData.amount} off
                </Typography>
              )}
            </Box>

            {/* Wallet */}
            <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: "rgba(146, 175, 93, 0.05)", border: "1px solid rgba(146, 175, 93, 0.1)" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Wallet Balance: ₹{user?.wallet_balance || 0}</Typography>
                <FormControlLabel
                  control={<Checkbox size="small" checked={useWallet} onChange={(e) => { setUseWallet(e.target.checked); if (!e.target.checked) setWalletAmount(""); }} />}
                  label={<Typography sx={{ fontSize: 13 }}>Use Wallet</Typography>}
                />
              </Box>
              {useWallet && (
                <Box>
                  <input
                    type="number"
                    placeholder="Enter amount to use"
                    value={walletAmount}
                    onChange={(e) => {
                      let val = e.target.value;
                      const balance = parseFloat(user?.wallet_balance || 0);
                      const payable = priceCalculation.payableBeforeWallet;
                      if (parseFloat(val) > balance) { toast.error("Insufficient balance"); val = balance.toString(); }
                      if (parseFloat(val) > payable) { toast.error("Wallet amount cannot exceed payable amount"); val = payable.toString(); }
                      setWalletAmount(val);
                    }}
                    style={{ padding: "6px 10px", borderRadius: "4px", border: "1px solid #ccc", width: "100%", fontSize: "13px" }}
                  />
                  {parseFloat(walletAmount) > parseFloat(user?.wallet_balance) && (
                    <Typography sx={{ fontSize: 11, color: "red", mt: 0.5 }}>Insufficient wallet balance</Typography>
                  )}
                </Box>
              )}
            </Box>

            {/* Discounts breakdown */}
            {priceCalculation.specialDiscount > 0 && (
              <Box sx={{ my: 0.3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ color: "grey", fontSize: { xs: 12, sm: 14, md: 16 } }}>Special Discount ({settings.discount_percentage}%):</Typography>
                <Typography sx={{ color: theme.palette.primary.main, fontSize: { xs: 12, sm: 14, md: 16 }, fontWeight: "bold" }}>(-) ₹{priceCalculation.specialDiscount.toFixed(2)}</Typography>
              </Box>
            )}
            {couponData && (
              <Box sx={{ my: 0.3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ color: "grey", fontSize: { xs: 12, sm: 14, md: 16 } }}>Coupon applied:</Typography>
                <Typography sx={{ color: theme.palette.primary.main, fontSize: { xs: 12, sm: 14, md: 16 }, fontWeight: "bold" }}>(-) ₹{couponData.amount}</Typography>
              </Box>
            )}
            {useWallet && walletAmount && (
              <Box sx={{ my: 0.3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ color: "grey", fontSize: { xs: 12, sm: 14, md: 16 } }}>Wallet used:</Typography>
                <Typography sx={{ color: theme.palette.primary.main, fontSize: { xs: 12, sm: 14, md: 16 }, fontWeight: "bold" }}>(-) ₹{walletAmount}</Typography>
              </Box>
            )}

            <Box sx={{ my: 0.3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ color: "grey", fontSize: { xs: 12, sm: 14, md: 16 } }}>Payable:</Typography>
              <Typography sx={{ color: theme.palette.primary.main, fontSize: { xs: 12, sm: 14, md: 16 }, fontWeight: "bold" }}>₹{finalPayableAmount}</Typography>
            </Box>

            <Box sx={{ my: 0.3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ color: "grey", fontSize: { xs: 12, sm: 14, md: 16 } }}>Service Charge ({serviceChargePercent}%):</Typography>
              <Typography sx={{ color: theme.palette.primary.main, fontSize: { xs: 12, sm: 14, md: 16 }, fontWeight: "bold" }}>+ ₹{serviceChargeAmount}</Typography>
            </Box>

            <Box sx={{ my: 0.3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ color: "grey", fontSize: { xs: 12, sm: 14, md: 16 } }}>Service Tax ({igstPercent}%):</Typography>
              <Typography sx={{ color: theme.palette.primary.main, fontSize: { xs: 12, sm: 14, md: 16 }, fontWeight: "bold" }}>+ ₹{igstAmount}</Typography>
            </Box>

            <Box sx={{ my: 0.3, display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1, pt: 1, borderTop: "1px dashed #ccc" }}>
              <Typography sx={{ color: "black", fontSize: { xs: 12, sm: 14, md: 16 }, fontWeight: "bold" }}>Total:</Typography>
              <Typography sx={{ color: theme.palette.primary.main, fontSize: { xs: 12, sm: 14, md: 16 }, fontWeight: "bold" }}>₹{totalAmount}</Typography>
            </Box>

            <Divider sx={{ borderStyle: "dashed", borderColor: "gray", borderWidth: 1, marginY: 2 }} />

            {/* Privacy */}
            {privacyContent && (
              <Box mb={2}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", mb: 0.5, display: "block" }}>Privacy Policy</Typography>
                <Box sx={{ maxHeight: 120, overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 1, p: 1.5, backgroundColor: "rgba(255,255,255,0.5)", fontSize: "0.75rem", "& p": { mb: 1 }, "& ul, & ol": { pl: 2, mb: 1 } }} dangerouslySetInnerHTML={{ __html: privacyContent }} />
              </Box>
            )}

            {/* Terms */}
            {termsContent && (
              <Box mb={2}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", mb: 0.5, display: "block" }}>Terms & Conditions</Typography>
                <Box sx={{ maxHeight: 120, overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 1, p: 1.5, backgroundColor: "rgba(255,255,255,0.5)", fontSize: "0.75rem", "& p": { mb: 1 }, "& ul, & ol": { pl: 2, mb: 1 } }} dangerouslySetInnerHTML={{ __html: termsContent }} />
              </Box>
            )}

            <Box display="flex" alignItems="center" sx={{ py: 2 }}>
              <Checkbox checked={acceptTerms} onChange={(e) => { setAcceptTerms(e.target.checked); setAcceptPrivacy(e.target.checked); }} />
              <Typography sx={{ fontSize: { xs: 12, sm: 14 } }}>
                I accept the{" "}
                <Link to="/pages/gioo_nano_plots_booking_privacy_policy" style={{ textDecoration: "none", color: "black", fontWeight: "bold" }}><strong>Privacy Policy</strong></Link>
                {" "}and{" "}
                <Link to="/pages/gioo_nano_plots_booking_terms_and_condition" style={{ textDecoration: "none", color: "black", fontWeight: "bold" }}><strong>Terms & Conditions</strong></Link>
              </Typography>
            </Box>

            <Typography align="center" sx={{ fontSize: { xs: 12, sm: 14, md: 15 }, color: "grey" }}>
              We will process your registration after payment
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Button
                sx={{ borderRadius: 20, px: 4, py: 0.5, backgroundColor: theme.palette.secondary.main, color: "black", fontSize: { xs: 14, sm: 16, md: 16 }, width: "auto" }}
                onClick={() => requireLogin(() => handleBuyGio(geoDetails?.id, unitsString))}
                disabled={!acceptTerms || !acceptPrivacy || (useWallet && parseFloat(walletAmount) > parseFloat(user?.wallet_balance)) || geoDetails?.sold_status === 1}
              >
                {geoDetails?.sold_status === 1 ? "Sold Out" : "Pay Now"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default Gioo_plots_property;